import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useSubscription } from "@apollo/client";
import { POST_QUERY, ADD_COMMENT, COMMENT_ADDED, VOTE } from "../graphql";

interface Comment {
  id: string;
  postId: string;
  parentId?: string | null;
  body: string;
  authorId: string;
  createdAt: string;
}

export function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refetch } = useQuery<{
    post: {
      id: string;
      title: string;
      url?: string | null;
      text?: string | null;
      score: number;
      commentCount: number;
      authorId: string;
      createdAt: string;
    };
    comments: Comment[];
  }>(POST_QUERY, { variables: { id }, skip: !id });

  const [comments, setComments] = useState<Comment[]>([]);
  const [voted, setVoted] = useState(false);

  useEffect(() => {
    if (data?.comments) setComments(data.comments);
  }, [data?.comments]);

  useSubscription<{ onCommentAdded: Comment }>(COMMENT_ADDED, {
    variables: { postId: id },
    skip: !id,
    onData: ({ data: sub }) => {
      const c = sub.data?.onCommentAdded;
      if (c)
        setComments((prev) =>
          prev.some((x) => x.id === c.id) ? prev : [...prev, c],
        );
    },
  });

  const [addComment, { loading: posting }] = useMutation(ADD_COMMENT);
  const [vote] = useMutation(VOTE);
  const [body, setBody] = useState("");

  if (loading) return <div className="card p-6 animate-pulse">loading…</div>;
  if (error || !data?.post) return <div className="card p-6">not found</div>;
  const p = data.post;

  return (
    <div className="space-y-6">
      <article className="card p-6 animate-slide-up">
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-center gap-1 shrink-0">
            <button
              onClick={() =>
                vote({
                  variables: { targetType: "POST", targetId: p.id },
                }).then(() => {
                  setVoted((v) => !v);
                  refetch();
                })
              }
              className={`grid place-items-center h-9 w-9 rounded-lg transition-colors
                ${
                  voted
                    ? "text-accent bg-accent/10"
                    : "text-mute hover:text-accent hover:bg-accent/10"
                }`}
              aria-label={voted ? "unvote" : "upvote"}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M12 4l8 10h-5v6h-6v-6H4z" />
              </svg>
            </button>
            <span className="text-sm text-white font-semibold tabular-nums">
              {p.score}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold leading-snug">
              {p.url && p.url.startsWith("http") ? (
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-accent-soft"
                >
                  {p.title}
                </a>
              ) : (
                p.title
              )}
            </h1>
            <div className="mt-1 text-xs text-mute flex gap-3 flex-wrap">
              <span className="text-white font-medium">{p.score} points</span>
              <span>
                by{" "}
                <Link to={`/u/${p.authorId}`} className="hover:text-white">
                  {p.authorId.slice(0, 8)}
                </Link>
              </span>
              <span>{timeAgo(p.createdAt)}</span>
              <span>{comments.length} comments</span>
              {p.url && <span className="chip">{hostOf(p.url)}</span>}
            </div>
            {p.text && (
              <p className="mt-4 text-mute-soft whitespace-pre-wrap">
                {p.text}
              </p>
            )}
          </div>
        </div>
      </article>

      <section className="card p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const value = body.trim();
            if (!value) return;
            addComment({ variables: { input: { postId: p.id, body: value } } });
            setBody("");
          }}
        >
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="input resize-y"
            placeholder="add a comment…"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="submit"
              disabled={posting || !body.trim()}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {posting ? "posting…" : "comment"}
            </button>
          </div>
        </form>
      </section>

      <ul className="space-y-2">
        {comments.map((c) => (
          <li key={c.id} className="card p-4 animate-slide-up">
            <div className="text-xs text-mute mb-2 flex gap-3">
              <Link to={`/u/${c.authorId}`} className="hover:text-white">
                {c.authorId.slice(0, 8)}
              </Link>
              <span>{timeAgo(c.createdAt)}</span>
            </div>
            <p className="text-mute-soft whitespace-pre-wrap leading-relaxed">
              {c.body}
            </p>
          </li>
        ))}
        {comments.length === 0 && (
          <li className="text-center text-sm text-mute py-6">
            No comments yet — be the first.
          </li>
        )}
      </ul>
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
